/**
 * This function adds a \ character before all the characters that need to be escaped
 * @param str string which needs to be changed
 */
function escapeRegexStr(str) {
    return str.replace(/([<>*()?\[\]+])/g, "\\$1");
}
/**
 * This regex checks for integers.
 *
 * - Tests true for literals like - `"", "2", "32434325435"`
 * - Tests false for literals like - `"a", "12as", "as23", "123.13"`
 */
export var integerExp = /^\d*$/;
export var watchlistNameExp = /^[a-zA-Z0-9_-]*$/;
/**
 * This regex checks for alphabets and spaces.
 *
 * - Tests true for literals like - `"", "a", "vfsgreftgr", "dsf csd"`
 * - Tests false for literals like - `"1", "12as", "as23", "s$ajkd"`
 */
export var alphabetExp = /^[a-zA-Z ]*$/;
/**
 * This regex checks for floating point numbers.
 *
 * If decimalPlaces is 2, then
 * - Tests true for literals like: `"", "1", "45243464", "3131.", "12312.2", "123432.34"`
 * - Tests false for literals like: `"a", "12ad", "as21"`
 *
 * @param decimalPlaces (optional) number of decimal places
 * @returns RegExp for checking floating point numbers
 */
export function floatNumExp(decimalPlaces) {
    if (decimalPlaces === void 0) { decimalPlaces = 2; }
    // if decimalPlaces === 2,
    // exp = ^\d*(?:\.\d{0,2})?$
    var exp = "^\\d*(?:\\.\\d{0,".concat(decimalPlaces, "})?$");
    return new RegExp(exp);
}
/**
 * This regex checks for alphanumeric characters.
 *
 * - Tests true for literals like - `"", "asd", "ASD", "123"`
 * - Tests false for literals like - `"_", "-", "$", ".", " ", [any other non-alphanumeric character]`
 */
export var alphaNumExp = /^[a-zA-Z0-9]*$/;
/**
 * This regex checks for emails.
 *
 * - Tests true for literals like - `"", "asd", "ASD", "123"`
 * - Tests false for literals like - `"_", "-", "$", ".", " ", [any other non-alphanumeric character]`
 */
export var emailRegex = /^[-_a-zA-Z0-9]+(\.[_a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*(\.[a-zA-Z]{2,4})$/;
/**
 * This regex checks if given string's length is between min and max characters.
 *
 * @param min min number of characters
 * @param max (optional) max number of characters. If not present then considered as infinity
 * @returns RegExp for checking if given string's length is between min and max characters
 */
export function betweenNChars(min, max) {
    return new RegExp("^.{" + min + "," + (max || "") + "}");
}
/**
 * This regex checks if string has atleast 1 character.
 *
 * - Tests true for literals like - `"A", "ASD", "1A23"`
 * - Tests false for literals like - `"abc", " ", "$", "123"`
 */
export var atleast1UpperChar = /[A-Z]/;
/**
 * This regex checks if string has atleast 1 digit.
 *
 * - Tests true for literals like - `"1", "123", "1A23"`
 * - Tests false for literals like - `"abc", " ", "$dfs", "ABC"`
 */
export var atleast1Digit = /\d/;
/**
 * This regex tests true if string is made from any character other than the mentioned characters.
 * If any mentioned character appears even once, then regex tests false
 *
 * - Tests true for literals like - `"", "$", "34", "saf&dfds", " "`
 * - Tests false for literals like - `"₹", "as₹fa", "DFS", "123"`
 */
export var atleast1SplChar = /[^0-9a-zA-Z]/;
/**
 * This regex tests true if string is made from any character other than the mentioned characters.
 * If any mentioned character appears even once, then regex tests false
 *
 * - Tests true for literals like - `"", "$", "34", "saf&dfds", " "`
 * - Tests false for literals like - `"₹", "as₹fa", "DFS", "123"`
 */
export var exceptSplChar = /^[^()<>?%+:;{}[\]\\|~`^\-‘",./₹]*$/;
/**
 * This regex checks for atleast 1 alphabet and 1 digit characters.
 *
 * - Tests true for literals like - `"a1", "1a", "sdfh23ejsd", "sdfh2-3ej$sd"`
 * - Tests false for literals like - `"", "-", "a", "1", "dfsdsd", "213213"`
 */
export var atleast1Alpha1Num = /(?=.*?[0-9])(?=.*?[A-Za-z]).+/;
/**
 * This regex checks if the string to be checked is not equal to str
 *
 * @param str the string that will be compared
 * @param flag regex flag
 * @returns RegExp for checking if the string to be checked is not equal to str
 */
export function notEqualTo(str, flag) {
    return new RegExp("^(?!" + str + "$).*$", flag);
}
/**
 * This regex checks if the string to be checked is equal to str
 *
 * @param str the string that will be compared
 * @param flag regex flag
 * @returns RegExp for checking if the string to be checked is equal to str
 */
export function equalTo(str, flag) {
    return new RegExp("^(?=" + escapeRegexStr(str) + "$).*$", flag);
}
/**
 * This regex checks if the string is a valid pan
 */
export var pan = /^[a-zA-Z]{5}[\d]{4}[a-zA-Z]{1}$/;
/**
 * This regex checks if the string is a valid phone/mobile number
 */
export var phoneNo = /^[6789]\d{9}$/;
/**
 * This regex checks if the string is a valid upi
 */
export var upiExp = /^[-.a-zA-Z0-9_]+@{1}[A-Za-z0-9]+$/;
export var incomeNetworth = /^[0-9]{0,10}[.]?[0-9]{0,2}$/;
export var detectIntWithSpace = /^(?=.*\d)[\d .]+$/;
export function specialCharExpression(min, max) {
    return new RegExp("(^[a-zA-Z0-9]*[^~`#$%^&*?\"',(){}<>\\[\\]:;|=+\\\\/]{".concat(min, ",").concat(max || "", "})+$"));
}
export var specialChars = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
/**
 * This regex checks if string has
 * 1) Limit 6-12 characters
 * 2) contains at least one lowercase
 * 3) contains at least Uppercase lowercase
 * 4) contains at least one special character
 * 5) contains at least one digit
 */
export var password = /^(?=.{6,12})(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^&+=])(?=.*[0-9]).*$/;
/**
 * This regex checks if string has atleast 1 character.
 *
 * - Tests true for literals like - `"a", "asd", "1a23"`
 * - Tests false for literals like - `"abc", " ", "$", "123"`
 */
export var atleast1LowerChar = /(?=.*[a-z])/;
/**
 * This reg ex checks if string char limit is between 6 and 12
 */
export var LimitCharBetweenSixTwelve = /^.{6,12}$/;
/**
 * A string consisting only of non-whitespaces
 */
export var whiteSpaceCheck = /^\S*$/;
export var onlyNumbersNotAllowed = /(?!^\d+$)^.+$/;
export var numbersWithCommas = /^(?:\d+|\d*\.\d+|(?:\d{1,2},)?(?:\d{2},)*\d{3}|(?:\d{1,2},)?(?:\d{2},)*\d{3}\.\d+)$/;
export var extractFileExtention = /(?:\.([^.]+))?$/;
